import { useEffect, useState } from 'react';
import { NewApiBaseUrl } from '../../consts/new-api-base-url';
import { buildConfigLocalizedUrl, getConfigLocaleHeaders } from '../../utils/config-localization';
import {
  MERGED_CRASH_CONFIGURATION_ORG_IDS,
  mergeFormConfigurations,
} from '../../utils/crash-form-sections';

const initUrl = new URL(`${NewApiBaseUrl}/Auth/Init`);

/**
 * @param {string} token
 * @param {number} organization
 */
async function fetchOrganizationInit(token, organization) {
  const newInitUrl = buildConfigLocalizedUrl(initUrl, { organization });
  const response = await fetch(newInitUrl, {
    method: 'GET',
    headers: getConfigLocaleHeaders({
      Authorization: `Bearer ${token}`,
    }),
  }).then((res) => res.json());

  if (response?.responseAnyError) return null;

  const payload = response?.responseData ?? response ?? {};
  if (payload.error || !payload.configuration) return null;
  return payload;
}

/**
 * Loads Police + Hospital + Insurance form configuration for merge forms.
 *
 * @param {string | null | undefined} token
 * @param {Record<string, unknown> | null | undefined} fallbackConfiguration
 */
export function useMergedMergeConfiguration(token, fallbackConfiguration) {
  const [mergedConfiguration, setMergedConfiguration] = useState(
    fallbackConfiguration ?? null,
  );
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) {
      setMergedConfiguration(fallbackConfiguration ?? null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const inits = await Promise.all(
          MERGED_CRASH_CONFIGURATION_ORG_IDS.map((orgId) =>
            fetchOrganizationInit(token, orgId),
          ),
        );
        if (cancelled) return;

        const validInits = inits.filter(Boolean);
        if (validInits.length > 0) {
          setMergedConfiguration(
            mergeFormConfigurations(validInits.map((init) => init.configuration)),
          );
        } else {
          setMergedConfiguration(fallbackConfiguration ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, fallbackConfiguration]);

  return {
    mergedConfiguration: mergedConfiguration ?? fallbackConfiguration ?? null,
    loading,
  };
}
